import { tv } from 'tailwind-variants';

export const learningMaterialsVariants = tv({
  slots: {
    wrapper: ['flex flex-col'],
    groupWrapper: ['flex flex-col'],
    groupTitle: ['text-[32px] leading-[38.4px] font-semibold text-primary', 'mb-[25px] pt-[45px]'],
    groupDescription: ['text-lg leading-[27px] text-light-black', 'font-light [--font-weight:300]'],
    typeWrapper: ['flex', 'flex-col'],
    typeTitle: ['text-[27.2px] font-bold! text-textPrimary', 'mt-4 mb-[19.2px]'],
    typeDescription: ['text-lg leading-[27px] text-light-black', 'font-light [--font-weight:300]'],
    cardsWrapper: ['flex', 'flex-col'],
  },
});

export const learningMaterialCardVariants = tv({
  slots: {
    card: ['flex flex-col', 'border border-medium-gray', 'overflow-hidden', 'bg-white', 'my-4'],
    headerBadge: [
      'bg-secondary',
      'text-white text-base leading-6 font-medium',
      'text-center',
      'py-1',
      'w-full',
    ],
    contentWrapper: ['flex flex-1 flex-row', 'pt-6 px-8 md:px-12 pb-6'],
    textContent: ['flex flex-col flex-1', 'flex-[0_0_66.66%]', 'pr-[15px]'],
    title: [
      'text-textPrimary [&_p]:text-textPrimary',
      'text-[20.8px] [--font-size:20.8px]',
      'leading-[31.2px] [--line-height:31.2px]',
      'font-medium [--font-weight:500]',
      'pb-4',
      '[&_p:last-child]:mb-0'
    ],
    label: [
      'mb-4',
      'text-xs font-medium leading-4.5 text-primary',
      'tracking-normal! uppercase',
      'tracking-wide',
    ],
    description: [
      'text-base [--font-size:16px]',
      'leading-6 [--line-height:24px]',
      'text-light-black',
      'flex-1',
      'font-light [--font-weight:300]',
    ],
    thumbnailWrapper: [
      'w-full',
      'flex justify-center md:justify-end items-start grow',
      'pl-[15px]',
    ],
    thumbnail: ['w-32 h-auto', 'object-contain aspect-auto'],
    // Wrap actions + error on their own lines so the message sits below the controls
    actionsWrapper: ['flex flex-wrap items-start', 'gap-3', 'pb-6 px-8 md:px-12'],
    quantityWrapper: ['flex', 'items-center', 'gap-2'],
    quantityLabel: ['text-base text-textPrimary font-medium'],
    quantityInput: [
      'w-[55px] h-[38px]',
      'px-3 py-1.5',
      'border border-medium-gray',
      'text-center',
      'text-base font-medium leading-6',
      'rounded',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-primary',
      'focus:border-primary',
      // Hide native number input spinners across all browsers
      '[appearance:textfield]',
      '[&::-webkit-outer-spin-button]:appearance-none',
      '[&::-webkit-inner-spin-button]:appearance-none',
    ],
    // Inline validation copy shown when the entered quantity exceeds the allowed maximum.
    // Uses an arbitrary hex (no error token in the theme) matching the CMS red palette value.
    quantityError: ['w-full basis-full', 'text-sm leading-5 font-medium', 'text-input-error!'],
    addToCartButton: [
      'w-[150px]',
      'min-h-[unset]',
      'inline-flex',
      'items-center',
      'justify-center',
      'px-3 py-1.5',
      'text-base font-medium leading-6',
      'text-white',
      'bg-primary',
      'border',
      'border-primary-border',
      'rounded',
      'hover:bg-primary-dark',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-primary',
      'transition-colors',
      // Dim and lock the button when there is nothing valid to add to the bag
      'disabled:opacity-[0.65]',
      'disabled:cursor-not-allowed',
      'disabled:hover:bg-primary',
    ],
    downloadButton: [
      'inline-flex',
      'items-center',
      'justify-center',
      'gap-[5px]',
      'px-4',
      'py-2',
      'text-base',
      'font-medium',
      'text-primary',
      'bg-white',
      'border',
      'border-primary',
      'rounded',
      'hover:bg-primary',
      'hover:text-white',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-primary',
      'transition-colors',
    ],
  },
  variants: {
    // Highlight the quantity field in red when the entered value breaks the max-quantity rule
    error: {
      true: {
        quantityInput: ['border-input-error', 'focus:ring-input-error', 'focus:border-input-error'],
      },
    },
  },
});

export const cartBarVariants = tv({
  slots: {
    bar: ['fixed bottom-0 inset-x-0 z-[10002]', 'flex justify-center', 'md:px-7.5'],
    inner: [
      'bg-primary text-white',
      'w-full max-w-[1140px]',
      'flex items-center justify-between gap-4',
      'px-12 py-4',
      'shadow-[0_-2px_8px_rgba(0,0,0,0.15)]',
    ],
    summaryText: ['text-xl leading-7.5 font-bold [--font-weight:700]'],
    viewButton: [
      'inline-flex items-center justify-center',
      'px-3 py-1.5',
      'text-base font-medium leading-6',
      'text-primary bg-white',
      'border border-white rounded',
      'hover:bg-light-gray',
      'focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-tertiary',
      'transition-colors',
      'cursor-pointer',
      'hover:bg-secondary hover:text-white hover:border-secondary',
    ],
  },
});

export const orderSummaryVariants = tv({
  slots: {
    overlay: ['fixed inset-0 z-[10002]', 'bg-tertiary/90', 'flex justify-start'],
    panel: [
      'bg-white',
      'overflow-y-auto',
      'relative',
      'mx-auto',
      'w-full max-w-page-template',
      'min-h-[100vh]',
      'flex flex-col',
    ],
    closeButtonWrapper: ['w-full', 'flex justify-end'],
    closeButton: [
      'ml-auto',
      'fixed top-0 z-10',
      'w-12 h-12 md:w-[68px] md:h-[68px]',
      'flex items-center justify-center',
      'bg-secondary text-white',
      'hover:bg-primary',
      'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white',
      'transition-colors',
    ],
    closeIcon: ['fa-whiteboard fa-semibold fa-xmark', 'text-4xl'],
    content: [
      'mx-auto',
      'flex flex-col',
      'px-8 md:px-20 py-12 md:py-16',
      'w-full max-w-component',
      'min-h-lvh',
    ],
    title: [
      'text-[40px]! md:text-[50px]! leading-[44px]! md:leading-[60px]! font-bold! text-textPrimary!',
      'mb-4',
    ],
    subtitle: ['text-2xl leading-9.5 font-light text-textPrimary', 'mb-[35px]'],
    infoBox: [
      'rounded',
      'text-lg leading-[27px] text-light-black font-medium',
      '[--font-size:18px] [--line-height:27px] [--font-weight:500]',
      'py-4 px-5 mb-4',
      // Need to use arbitrary values as for bg and border color
      // as there are no near match colors found in figma
      'bg-[#d1ecf1] border border-[#bee5eb]',
    ],
    infoLink: ['text-secondary underline hover:no-underline'],
    table: ['w-full border-collapse', 'mb-5.5', 'learning-material-items-table'],
    headRow: ['border-y border-medium-gray border-b-2'],
    th: ['text-left', 'text-primary font-bold text-base leading-6', 'p-3'],
    thRemove: ['text-right'],
    bodyRow: ['border-t border-medium-gray', 'odd:bg-light-gray/80'],
    tdQuantity: ['py-4 px-4', 'align-middle', 'w-[90px]'],
    tdItem: [
      'p-3',
      'align-middle',
      '[&_p]:mb-0',
      'text-base [--font-size:16px]',
      'font-medium [--font-weight:500]',
      'leading-6 [--line-height:24px]',
      'text-textPrimary [&_p]:text-textPrimary',
    ],
    tdRemove: ['py-4 px-4', 'align-middle', 'text-right', 'w-[80px]'],
    quantityInput: [
      'bg-white',
      'w-[55px] h-[38px]',
      'px-3 py-1.5',
      'border border-light-black rounded',
      'text-center text-base font-medium leading-6',
      'focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
      '[appearance:textfield]',
      '[&::-webkit-outer-spin-button]:appearance-none',
      '[&::-webkit-inner-spin-button]:appearance-none',
    ],
    // Inline per-row validation copy shown when an edited quantity exceeds the allowed maximum
    quantityError: ['mt-1', 'text-sm leading-5 font-medium', 'text-input-error!'],
    removeButton: [
      'inline-flex items-center justify-center',
      // 'w-6 h-6',
      'text-xl text-slider-thumb-button-border',
      'cursor-pointer',
    ],
    removeButtonIcon: ['fa-jelly fa-regular fa-circle-xmark'],
    emptyMessage: ['text-base leading-6 text-light-black', 'mb-6'],
    formWrapper: [
      'w-full',
      '[&>section]:px-0!'
    ],
    browseLink: [
      'py-1.5 px-3',
      'text-base font-medium leading-6',
      'text-primary-border hover:text-dark-blue',
      'hover:underline cursor-pointer',
      'self-start',
      'transition-colors',
    ],
  },
  variants: {
    // Highlight a summary row's quantity field in red when its value breaks the max-quantity rule
    error: {
      true: {
        quantityInput: ['border-input-error', 'focus:ring-input-error', 'focus:border-input-error'],
      },
    },
    // Keep the panel mounted (so form state survives) but visually/functionally hidden while closed
    hidden: {
      true: {
        overlay: ['hidden'],
      },
    },
  },
});
